<template>
  <v-dialog
    :id="`${id}-modal`"
    ref="modal"
    class="modal"
    :eager="true"
    :persistent="true"
    :scrollable="true"
    :no-click-animation="true"
    :model-value="isVisible"
    :content-class="{ loading: isDataLoading, updating: inProgress }"
    :static="isStatic"
    :retain-focus="!firstInputFocus"
    :class="dialogSize">
    <div class="modal-header">
      <div class="d-flex header-container">
        <h4
          v-if="title"
          :title="title"
          class="qx-dialog-title">
          {{ title }}
        </h4>
        <slot name="modal-header" />
      </div>
      <span class="modal-icons">
        <em
          class="close qx-icon"
          :class="{
            'icon-cancel': closeStyle === 'close',
            'icon-min': closeStyle === 'minimize',
          }"
          @click="closeModal" />
      </span>
    </div>
    <div
      ref="body"
      class="modal-body"
      :class="{ scrollable: isScrollable, loading: isLoading, 'mb-15': !hideFooter }">
      <qx-spinner
        v-if="isLoading"
        :id="`${id}-modal-spinner`"
        :global="isDataLoading" />
      <div
        ref="body"
        class="modal-body-container"
        :class="{ loading: isLoading }">
        <valid-form
          v-if="submitButton"
          ref="observer"
          tabindex="1"
          @keydown.enter.prevent>
          <slot name="body" />
        </valid-form>
        <slot
          v-else
          name="body" />
      </div>
    </div>
    <div
      v-if="!hideFooter"
      class="modal-footer">
      <slot name="modal-footer">
        <div
          class="d-flex w-100"
          :class="{ 'justify-space-between': leftButtons, 'justify-end': !leftButtons }">
          <template v-if="buttonsGroup">
            <qx-button-group
              v-for="(button, key) in buttonsGroup"
              :id="key"
              :key="key"
              :disabled="button.disabled || isLoading"
              :state="button.state"
              :title="button.title"
              :action="button.action"
              :subbuttons="button.subbuttons" />
          </template>
          <div
            v-if="leftButtons"
            class="left-buttons-block">
            <qx-button
              v-for="(button, key) in leftButtons"
              :id="`${id}-${key}`"
              :key="key"
              :disabled="button.disabled || isLoading"
              :invisible="button.invisible"
              :state="button.state || 'border-blue'"
              :title="button.icon ? '' : button.title"
              :size="button.size"
              :classes="button.classes"
              :action="() => buttonAction(button)">
              <template
                v-if="button.icon"
                #button-content>
                <div class="button-content d-flex">
                  <em
                    :title="button.title"
                    class="action-button-icon"
                    :class="button.icon"
                    aria-hidden="true" />
                  <span>{{ button.title }}</span>
                </div>
              </template>
            </qx-button>
          </div>

          <div
            v-if="buttons"
            class="buttons-block">
            <qx-button
              v-for="(button, key) in buttons"
              :id="`${id}-${key}`"
              :key="key"
              :disabled="button.disabled || (isLoading && !button.isAlwaysEnable)"
              :invisible="button.invisible"
              :state="button.state"
              :title="button.icon ? '' : button.title"
              :size="button.size"
              :action="() => buttonAction(button)"
              v-bind="bindTooltipToButton(button)">
              <template
                v-if="button.icon"
                #button-content>
                <div class="button-content d-flex align-center">
                  <em
                    :title="button.title"
                    class="action-button-icon"
                    :class="button.icon"
                    aria-hidden="true" />
                  <span>{{ button.title }}</span>
                </div>
              </template>
            </qx-button>
          </div>
        </div>
      </slot>
    </div>
  </v-dialog>

  <qx-dialog
    v-if="modalStates.warning"
    id="warning"
    :is-visible="modalStates.warning"
    :title="string.warningTitle"
    :buttons="warningActionButtons"
    type="small"
    @close="closeWarning">
    <template #body>
      <span>{{ string.warningMessage }}</span>
    </template>
  </qx-dialog>
</template>

<script>
import { Form as ValidForm } from 'vee-validate';
import isEmpty from 'lodash/isEmpty';
import QxSpinner from '@/elements/spinner.vue';
import QxButton from '@/elements/button.vue';
import QxButtonGroup from '@/elements/QxButtonGroups.vue';

import { isMobile } from '@/elements/utils.js';

const KEY_CODES = {
  enter: 13,
  esc: 27,
};

export default {
  name: 'QxDialog',
  components: {
    QxSpinner,
    QxButton,
    ValidForm,
    QxButtonGroup,
  },
  provide() {
    return {
      modal: {
        isVisible: this.isModalVisible.bind(this),
      },
    };
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      validator: (value) => ['small', 'large', 'medium', 'x-large'].includes(value),
      default: 'medium',
    },
    id: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      default: '',
    },
    buttons: {
      type: Object,
      default: () => {},
    },
    buttonsGroup: {
      type: Object,
      default: () => {},
    },
    leftButtons: {
      type: Object,
      default: () => {},
    },
    closeStyle: {
      type: String,
      default: 'close',
      validator: (value) => ['close', 'minimize'].includes(value),
    },
    closeAction: {
      type: Function,
      default: null,
    },
    // not transparent spinner
    isDataLoading: {
      type: Boolean,
      default: false,
    },
    // transparent spinner
    inProgress: {
      type: Boolean,
      default: false,
    },
    hideFooter: {
      type: Boolean,
      default: false,
    },
    isStatic: {
      type: Boolean,
      default: false,
    },
    manualControl: {
      type: Boolean,
      default: false,
    },
    isScrollable: {
      type: Boolean,
      default: true,
    },
    centered: {
      type: Boolean,
      default: false,
    },
    firstInputFocus: {
      type: Boolean,
      default: true,
    },
    warnOnClose: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['shown', 'close'],
  data() {
    return {
      hasTextarea: false,
      modalStates: {
        warning: false,
      },
    };
  },
  computed: {
    string() {
      return {
        warningTitle: this.$gettext('Generation in progress'),
        warningMessage: this.$gettext(
          'It may take a few minutes. If you close the window, the generation will be interrupted. Would you like to continue generation?'
        ),
        warningError: this.$gettext('Generation interrupted.'),
      };
    },
    warningActionButtons() {
      return {
        generate: {
          state: 'active',
          title: this.$gettext('Yes'),
          action: this.closeWarning,
        },
        close: {
          state: 'passive',
          title: this.$gettext('Interrupt'),
          action: this.interruptAction,
        },
      };
    },

    isLoading() {
      return this.isDataLoading || this.inProgress;
    },
    dialogSize() {
      if (!this.type || this.type === 'medium') {
        return 'modal-md';
      }
      if (this.type === 'large') {
        return 'modal-lg';
      }
      if (this.type === 'x-large') {
        return 'modal-xl';
      }
      return 'modal-sm';
    },
    hotkeyTooltip() {
      return this.$gettextInterpolate(
        this.$gettext('%{divOpen}%{strongOpen}Submit form%{strongClose} %{br} Press %{ hotkey }%{divClose}'),
        {
          divOpen: '<div class="text-center">',
          divClose: '</div>',
          strongOpen: '<strong>',
          strongClose: '</strong>',
          br: '<br/>',
          hotkey: `${this.submitFormHotKey}`,
        },
        true
      );
    },
    submitButton() {
      // If exist button with type submit modal body behaves as form with buttons, otherwise it is just slot
      return Object.values(this.buttons || {}).find((button) => button.type === 'submit');
    },
    submitFormHotKey() {
      if (!this.hasTextarea) {
        return 'Enter';
      }
      let os = navigator.userAgent;
      if (isMobile()) {
        return null;
      }
      if (os.includes('Mac')) {
        return 'Cmd+Enter';
      }
      return 'Ctrl+Enter';
    },
    modalContent() {
      return this.$refs?.body;
    },
  },
  watch: {
    isVisible: {
      handler(val) {
        if (val) {
          this.shownModal();
        }
      },
      immediate: true,
    },
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeydown);
  },
  methods: {
    async shownModal() {
      await this.$nextTick();
      this.hasTextarea = !isEmpty(this.findTextarea());
      document.addEventListener('keydown', this.handleKeydown);
      if (this.firstInputFocus) {
        const input = this.modalContent.querySelector('input, textarea, select');
        if (input) {
          input.focus();
        } else {
          this.$refs[`${this.id}-modal-form`]?.focus();
        }
      }
      this.$emit('shown');
    },
    findTextarea() {
      return this.modalContent.querySelector('textarea');
    },
    async buttonAction(button) {
      if (button.type === 'submit') {
        const valid = await this.triggerValidate();
        if (!valid) {
          return;
        }
        this.submitButton.action();
        return;
      }
      if (button.type === 'cancel') {
        this.closeModal();
        return;
      }
      button.action();
    },
    interruptAction() {
      this.$notify({
        text: this.string.warningError,
        type: 'error',
      });
      this.$emit('close');
    },
    closeWarning() {
      this.modalStates.warning = false;
    },
    closeModal() {
      if (this.warnOnClose) {
        this.modalStates.warning = true;
        return;
      }
      if (this.closeAction) {
        this.closeAction();
        return;
      }
      this.$emit('close');
    },
    bindTooltipToButton(button) {
      if (button.state !== 'active') {
        return null;
      }
      return {
        tooltipTitle: this.hotkeyTooltip,
        tooltipPosition: 'top',
      };
    },
    async handleKeydown(e) {
      const textAreaCondition = this.hasTextarea ? e.ctrlKey || e.metaKey : true;
      if (e.keyCode === KEY_CODES.enter && textAreaCondition) {
        const valid = await this.triggerValidate();
        if (!valid) {
          return;
        }
        if (this.submitButton) {
          this.submitButton.action();
        } else {
          const activeButton = Object.values(this.buttons || {}).find((button) => button.state === 'active');
          activeButton?.action();
        }
      } else if (e.keyCode === KEY_CODES.esc) {
        this.closeModal();
      }
    },
    async triggerValidate() {
      if (this.$refs.observer) {
        const { valid } = await this.$refs.observer.validate();
        return valid;
      }
      return true;
    },
    isModalVisible() {
      return this.isVisible;
    },
  },
};
</script>

<style lang="scss">
.modal {
  align-items: flex-start;
  &.modal-sm {
    .v-overlay__content {
      width: 410px;
      max-width: 410px;
    }
  }
  &.modal-md {
    .v-overlay__content {
      width: 600px;
      max-width: 600px;
    }
  }
  &.modal-lg {
    .v-overlay__content {
      width: 800px;
      max-width: 800px;
    }
  }
  &.modal-xl {
    .v-overlay__content {
      max-width: 1100px;
    }
  }
  .v-overlay__scrim {
    background: black;
    opacity: 0.5;
  }
  .v-overlay__content {
    position: absolute;
    top: 0;
    left: auto;
    max-height: 90vh;
    overflow: visible;
    border-radius: 6px;
    background: #fff;
    box-shadow: 2px 5px 20px 0px rgba(127, 147, 165, 0.3);
  }
  .modal-header {
    font: 24px/32px $semi-bold-font;
    color: $base-grey;
    align-items: center;
    padding: 10px 1rem;
    border-bottom: $section-border;
    position: relative;
    line-height: 32px;
    min-height: 56px;
    display: flex;
    .close {
      position: absolute;
      top: 0px;
      right: 0px;
      padding: 16px;
    }
    .header-container {
      width: calc(100% - 40px);
    }
    .modal-icons {
      color: $icon-grey;
      font-size: 15px;
      cursor: pointer;
    }
    .modal-title {
      @extend %ellipsisOverflow;
      font-size: 24px;
    }
  }
  .modal-body {
    position: relative;
    padding: 1rem;
    &.scrollable {
      overflow-y: auto;
    }
    &.loading {
      // overflow: hidden;
    }
  }
  .spinner-layout {
    position: fixed;
    left: 0;
    right: 0;
    top: 56px;
    bottom: 60px;
    height: auto;
  }
  .spinner {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    margin: 0;
  }
  .modal-body-container {
    position: relative;
    height: 100%;
    &.loading,
    &.updating {
      overflow: hidden;
    }
    select:focus {
      outline: 1px solid $base-orange;
    }
  }
  .modal-footer {
    position: absolute;
    bottom: 0;
    width: 100%;
    padding: 0.75rem;
    border-top: $section-border;
    .left-buttons-block {
      .button-content {
        align-items: flex-end;
      }
    }
    .buttons-block {
      @extend %dspFlex;
      @include flex-direction(row-reverse);
      button {
        margin-left: 6px;
      }
    }
    > div.spinner {
      margin: 10px auto;
      float: left;
      width: 100%;
    }
    .button-content {
      gap: 5px;
      .qx-icon {
        font-size: 22px;
      }
    }
  }
}

.tip {
  flex: 1;
  align-items: center;
  margin-right: 10px;
  font-size: 12px;
  color: $label-grey;
  display: flex;
}
.hot-key {
  display: inline-block;
  margin-left: 3px;
  margin-right: 5px;
  padding: 1px 6px;
  border-radius: 4px;
  font-weight: 700;
}
</style>
